// Implements: F-UIX-024, F-UIX-009, F-UIX-010
/**
 * The applied design of the mockup on the page, and the explicit re-pin.
 *
 * The page mounts it with `key` = the mockup id (checklist 14): the pending
 * state, the refusal and the "now uses version N" notice belong to ONE mockup,
 * and a re-pin that answers after the person moved to another mockup has no
 * container left to write into; its view goes to its own mockup's cache entry.
 * The re-pin is sent only when the person asks, once (`retry: false`), to the
 * latest version the server offered: the profile's, or the system default's.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { AppliedProfileIndicator } from "../components/AppliedProfileIndicator";
import { useMockupDesign, useRepinMockup } from "../hooks/useMockupDesign";

export interface AppliedProfileIndicatorContainerProps {
  readonly mockupId: string;
}

export function AppliedProfileIndicatorContainer({
  mockupId,
}: AppliedProfileIndicatorContainerProps): ReactElement {
  const { t } = useTranslation();
  const design = useMockupDesign(mockupId);
  const repin = useRepinMockup();
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  const [repinnedVersion, setRepinnedVersion] = useState<number | null>(null);

  if (design.isError) {
    // The generic state says that something failed; the sentence above it says what.
    return (
      <div>
        <p>{t("design.indicator.failed")}</p>
        <ErrorState
          error={design.error}
          onRetry={() => {
            void design.refetch();
          }}
        />
      </div>
    );
  }
  if (design.data === undefined) return <LoadingState label={t("common.state.loading")} />;
  const view = design.data;

  const submit = async (target: number): Promise<void> => {
    setPending(true);
    setRefusal(undefined);
    setRepinnedVersion(null);
    const outcome = await repin(mockupId, { profile_id: view.profile_id, version: target });
    setPending(false);
    if (!outcome.ok) {
      setRefusal(userMessage(outcome.error, t("design.indicator.repinFailed")));
      return;
    }
    setRepinnedVersion(outcome.view?.version ?? null);
  };

  return (
    <AppliedProfileIndicator
      view={view}
      onRepin={() => {
        // The action is only offered with a latest version to move to.
        if (view.latest_version !== null) void submit(view.latest_version);
      }}
      pending={pending}
      refusal={refusal}
      repinnedVersion={repinnedVersion}
    />
  );
}
